/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {ChangeDetectorRef, EventEmitter, OnDestroy, Pipe, PipeTransform, WrappedValue, ɵisObservable, ɵisPromise, PLATFORM_ID, Inject} from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import {Observable} from 'rxjs';
import {HttpClient} from "@angular/common/http";
import { TimerObservable } from "rxjs/observable/TimerObservable";
import {Subject} from 'rxjs/Subject';
import {ISubscription, Subscription} from 'rxjs/Subscription';
import {Type, ɵstringify as stringify} from '@angular/core';
import 'rxjs/add/observable/forkJoin';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
//import { DataService } from "angular-engine/engine/service/data/api.data.service";
import { CoreEffects } from 'angular-engine/engine/state/effect/core.effects';
import { APP_SETTINGS } from 'angular-engine/modules/ng-express-engine/tokens';


interface SubscriptionStrategy {
  createSubscription(async: Observable<any>|Promise<any>, updateLatestValue: any): ISubscription
      |Promise<any>;
  dispose(subscription: ISubscription|Promise<any>): void;
  onDestroy(subscription: ISubscription|Promise<any>): void;
}

class ObservableStrategy implements SubscriptionStrategy {
  createSubscription(async: Observable<any>, updateLatestValue: any): ISubscription {
    return async.subscribe({next: updateLatestValue, error: (e: any) => { throw e; }});
  }

  dispose(subscription: ISubscription): void { subscription.unsubscribe(); }

  onDestroy(subscription: ISubscription): void { subscription.unsubscribe(); }
}

class PromiseStrategy implements SubscriptionStrategy {
  createSubscription(async: Promise<any>, updateLatestValue: (v: any) => any): Promise<any> {
    return async.then(updateLatestValue, e => { throw e; });
  }

  dispose(subscription: Promise<any>): void {}

  onDestroy(subscription: Promise<any>): void {}
}

const _promiseStrategy = new PromiseStrategy();
const _observableStrategy = new ObservableStrategy();

/**
 * @ngModule CommonModule
 * @whatItDoes Unwraps a value from an asynchronous primitive.
 * @howToUse `observable_or_promise_expression | async`
 * @description
 * The `async` pipe subscribes to an `Observable` or `Promise` and returns the latest value it has
 * emitted. When a new value is emitted, the `async` pipe marks the component to be checked for
 * changes. When the component gets destroyed, the `async` pipe unsubscribes automatically to avoid
 * potential memory leaks.
 *
 *
 * ## Examples
 *
 * This example binds a `Promise` to the view. Clicking the `Resolve` button resolves the
 * promise.
 *
 * {@example common/pipes/ts/async_pipe.ts region='AsyncPipePromise'}
 *
 * It's also possible to use `async` with Observables. The example below binds the `time` Observable
 * to the view. The Observable continuously updates the view with the current time.
 *
 * {@example common/pipes/ts/async_pipe.ts region='AsyncPipeObservable'}
 *
 * @stable
 */
@Pipe({name: 'asyncTimer', pure: false})
export class AsyncTimerPipe implements OnDestroy, PipeTransform {
  private _latestValue: any = null;
  private _latestReturnedValue: any = null;
  private ngUnsubscribe:Subject<any> = new Subject<any>();;
  private timedGets: Observable<Object>[] = [];
  private getTimer: Subscription;

  private _subscription: ISubscription|Promise<any>|null = null;
  private _obj: Observable<any>|Promise<any>|EventEmitter<any>|null = null;
  private _strategy: SubscriptionStrategy = null !;
  private isServer = false;

  constructor(
    @Inject(PLATFORM_ID) platformId: string,
    @Inject(APP_SETTINGS) private settings: any,
    private _ref: ChangeDetectorRef, 
    private router: Router,
    private activeRoute: ActivatedRoute,
    //private http: DataService,
    private coreEffects: CoreEffects
  ) {
        this.isServer = isPlatformServer(platformId);
    }

  ngOnDestroy(): void {
    if (this._subscription) {
      this._dispose();
    }
  }

  transform<T>(obj: null): null;
  transform<T>(obj: undefined): undefined;
  transform<T>(obj: Observable<T>|null|undefined): T|null;
  transform<T>(obj: Promise<T>|null|undefined): T|null;
  transform(obj: Observable<any>|Promise<any>|null|undefined): any {
    if (!this._obj) {
      if (obj) {
        this._subscribe(obj);
      }
      this._latestReturnedValue = this._latestValue;
      return this._latestValue;
    }

    if (obj !== this._obj) {
      this._dispose();
      return this.transform(obj as any);
    }

    if (this._latestValue === this._latestReturnedValue) {
      return this._latestReturnedValue;
    }

    this._latestReturnedValue = this._latestValue;
    return WrappedValue.wrap(this._latestValue);
  }

  private _subscribe(obj: Observable<any>|Promise<any>|EventEmitter<any>): void {
    this._obj = obj;
    this._strategy = this._selectStrategy(obj);
    this._subscription = this._strategy.createSubscription(
        obj, (value: Object) => this._updateLatestValue(obj, value));
  }

  private _selectStrategy(obj: Observable<any>|Promise<any>|EventEmitter<any>): any {
    if (ɵisPromise(obj)) {
      return _promiseStrategy;
    }

    if (ɵisObservable(obj)) {
      return _observableStrategy;
    }

    throw invalidPipeArgumentError(AsyncTimerPipe, obj);
  }

  private _dispose(): void {
    this._strategy.dispose(this._subscription !);
    this._latestValue = null;
    this._latestReturnedValue = null;
    this._subscription = null;
    this._obj = null;

    if(this.ngUnsubscribe)
    {
            this.ngUnsubscribe.next();
            this.ngUnsubscribe.complete();
            this.ngUnsubscribe.unsubscribe();
    }

    this.timedGets = [];
    
    if(this.getTimer)
        this.getTimer.unsubscribe();
    
    this.getTimer = null;
  }

  private _updateLatestValue(async: any, value: Object): void {
    if (async === this._obj) {
      this._latestValue = value;
      if(!this.isServer && value)
      {
        if(value["meta"])
        {
          const meta:any = value["meta"];
  
          if(meta.poll)
          {
              this.createTimedGet(meta.details_url);
          }
        }
        
        if(value["rows"])
        {
           const rows:any[] = value["rows"];
  
           for(let row of rows)
           {
              const meta = row.meta;
  
              if(meta.poll)
              {
                  this.createTimedGet(meta.details_url);
              }
           }
        }
      }
      

      this._ref.markForCheck();
    }
  }

  private createTimedGet(url){
    this.timedGets.push(this.coreEffects.getFreshData(url).takeUntil(this.ngUnsubscribe))
    
    if(!this.getTimer)
    {
        this.createGetTimer();
    }
  }

  private createGetTimer(){
    this.getTimer = TimerObservable.create(0, this.settings['AUTO_REFRESH_INTERVAL'])
    .takeUntil(this.ngUnsubscribe)
    .subscribe(() => {
        Observable.forkJoin(
            ...this.timedGets
          ).subscribe(results =>{
            var dirty = false;

            let resultDictionary = {};
            results.forEach((obj) => {
                const meta:any = obj["meta"];
                if(meta.unique_id)
                {
                    resultDictionary[meta.unique_id] = obj;
                }
            });
            const url  = (this.router.url == "/") ? this.settings['HOME_ROUTE'] : this.router.url;
            const params = this.activeRoute.snapshot.params;
            for(let obj in this._latestValue)
            {
                switch(obj)
                {
                    case "meta" : {
                        if(resultDictionary[this._latestValue[obj]["unique_id"]])
                        {
                            if(this._latestValue[obj]["updated_at"] !== resultDictionary[this._latestValue[obj]["unique_id"]]["meta"]["updated_at"])
                            {
                                dirty = true;
                                this._latestValue[obj] = resultDictionary[this._latestValue[obj]["unique_id"]];
                            }
                        }
                    }
                    break;
                    case "rows" : {
                        for(let i = 0; i < this._latestValue[obj].length; i++)
                        {
                            const meta:any = this._latestValue[obj][i].meta;
                            if(resultDictionary[meta.unique_id])
                            {
                                if(meta["updated_at"] !== resultDictionary[meta.unique_id]["meta"]["updated_at"])
                                {
                                    dirty = true;
                                    //Don't let section overrides go away  
                                    this._latestValue[obj][i] = this.coreEffects.handleInternalState(resultDictionary[meta.unique_id], params, url, false, false, this._latestValue[obj][i]["meta"]);                                   
                                    //this._latestValue[obj][i] = this.coreEffects.keepOverrides(this._latestValue[obj][i]["meta"], resultDictionary[meta.unique_id]);
                                }                  
                            }
                        }
                    }
                    break;
                }
            }

            if(dirty)
            {
                //UGLY but... Object.assign is not deep, the result is new but object properties inside are not
                this._latestValue =  JSON.parse(JSON.stringify(this._latestValue));//Object.assign({}, this._latestValue);
                this._ref.markForCheck();
            }
                

          });
    });
  }
  
}

function invalidPipeArgumentError(type: Type<any>, value: Object) {
    return Error(`InvalidPipeArgument: '${value}' for pipe '${stringify(type)}'`);
}


// WEBPACK FOOTER //
// ./src/angular-shared-features/feature/shared-pipe/async-timer.pipe.ts